import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql, useLazyLoadQuery } from "react-relay"
import { isFuture } from "date-fns"
import { FilterableSelect } from "@/components/filterable-select"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { DatePicker } from "@/components/ui/date-picker"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { Input, TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { textAreaDefaultMaxLength } from "@/models/common"
import { chEditBillingSectionQuery } from "@/modules/common/billing/components/billing-form/ch/edit/__generated__/chEditBillingSectionQuery.graphql"
import { ChEditBillingSchemaType } from "@/modules/common/billing/components/billing-form/ch/edit/schema/ch-edit-billing-schema"
import { EMPLOYER_INSURANCE } from "@/modules/common/billing/components/billing-form/constants"
import { Law, NotBillableReasonValues, TreatmentReasonValues } from "@/types/__generated__/union-types"

const chEditBillingSectionQueryDescriptor = graphql`
  query chEditBillingSectionQuery($order: [OrganisationSortInput!], $where: OrganisationFilterInput) {
    ch_organisations(order: $order, where: $where) {
      id
      name
    }
  }
`

const LAW_TYPES = ["KVG", "UVG", "IVG", "MVG"] as const satisfies ReadonlyArray<Law>
type InsuranceOption = {
  name: string
  id: string
}

const ChEditBillingSection = ({
  disabled,
  isAdministrativeRequest,
}: {
  disabled?: boolean
  isAdministrativeRequest?: boolean
}) => {
  const { t } = useTranslation()

  const {
    register,
    setValue,
    resetField,
    control,
    formState: { errors },
  } = useFormContext<ChEditBillingSchemaType>()

  const { ch_organisations } = useLazyLoadQuery<chEditBillingSectionQuery>(chEditBillingSectionQueryDescriptor, {
    order: [{ name: "ASC" }],
    where: { lawType: { eq: "UVG" } },
  })

  const isNotBillable = useWatch({ control, name: "isNotBillable" })
  const notBillableReason = useWatch({ control, name: "notBillableReason" })
  const dateOfAccident = useWatch({ control, name: "dateOfAccident" })
  const lawType = useWatch({ control, name: "lawType" })

  const clearUvgFields = () => {
    resetField("dateOfAccident", { defaultValue: undefined })
    resetField("organisationId", { defaultValue: undefined })
    resetField("accidentReference", { defaultValue: undefined })
  }
  const clearIvgFields = () => resetField("ivgCaseReference", { defaultValue: undefined })
  const clearMvgFields = () => resetField("mvgCaseReference", { defaultValue: undefined })

  const accidentInsuranceGroups = [
    {
      heading: "",
      options: [{ id: EMPLOYER_INSURANCE, name: t("patient.conclude.billing.accidentInsurances.employer") }],
    },
    {
      heading: t("patient.conclude.billing.other"),
      options: ch_organisations.map<InsuranceOption>((item) => ({ id: item.id, name: item.name })),
    },
  ]

  const getInsuranceNameByOrgName = (id: string) =>
    accidentInsuranceGroups.flatMap((group) => group.options).find((option) => option.id === id)?.name

  return (
    <div className="flex flex-col gap-4">
      <div className="text-base font-medium" data-testid="billing-description">
        {t("patient.conclude.billing.billing")}
      </div>
      {!isAdministrativeRequest && (
        <div className="flex justify-between" data-testid="billable-section">
          <FormField
            control={control}
            name="isNotBillable"
            render={({ field: { value, onChange } }) => (
              <DetailCheckbox
                checked={value}
                data-testid="not-billable-checkbox"
                onCheckedChange={onChange}
                disabled={disabled}>
                {t("patient.conclude.billing.notBillable")}
              </DetailCheckbox>
            )}
          />
        </div>
      )}
      {isNotBillable ? (
        <>
          <FormField
            key="not-billable-reason"
            control={control}
            name="notBillableReason"
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                className="gap-4"
                onValueChange={onChange}
                defaultValue={value}
                value={value}
                data-testid="not-billable-reasons"
                disabled={disabled}>
                {NotBillableReasonValues.map((notBillableReason) => (
                  <RadioGroupItem
                    value={notBillableReason}
                    key={notBillableReason}
                    data-testid={notBillableReason.toLowerCase()}>
                    <RadioGroupLabel>
                      {t(`patient.conclude.billing.notBillableReasonList.${notBillableReason}`)}
                    </RadioGroupLabel>
                  </RadioGroupItem>
                ))}
              </RadioGroup>
            )}
          />
          {notBillableReason === "OTHER_REASON" && (
            <TextArea
              {...register("reason")}
              label={t("patient.conclude.billing.reason")}
              error={errors.reason?.message}
              data-testid="reason-input"
              translateError
              required
              textLimit={textAreaDefaultMaxLength}
              disabled={disabled}
            />
          )}
        </>
      ) : (
        <>
          <FormField
            key="treatment-reason"
            control={control}
            name="treatmentReason"
            render={({ field: { name, value, onChange }, formState: { errors }, fieldState: { invalid } }) => {
              const isKnownTreatmentReason = !!value && TreatmentReasonValues.includes(value)
              const selectedTreatmentReasonLabel = isKnownTreatmentReason
                ? t(`patient.conclude.billing.treatmentReasonList.${value}`)
                : undefined

              return (
                <div>
                  <Select value={value} onValueChange={onChange} disabled={disabled}>
                    <SelectTrigger hasError={invalid} data-testid="treatment-reason-select">
                      <SelectValue asChild placeholder={t("patient.conclude.billing.treatmentReason") + "*"}>
                        <LabelAndSelectValue
                          data-testid="treatment-reason"
                          label={t("patient.conclude.billing.treatmentReason") + "*"}
                          value={selectedTreatmentReasonLabel}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {TreatmentReasonValues.map((treatmentReason) => (
                        <SelectItem
                          value={treatmentReason}
                          key={treatmentReason}
                          data-testid={`treatment-reason-${treatmentReason.toLocaleLowerCase()}`}>
                          {t(`patient.conclude.billing.treatmentReasonList.${treatmentReason}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <TranslatedFieldError errors={errors} name={name} />
                </div>
              )
            }}
          />
          <FormField
            control={control}
            name="lawType"
            render={({ field: { value, onChange }, fieldState: { invalid } }) => (
              <div>
                <Select
                  value={value ?? ""}
                  onValueChange={(v: Law) => {
                    onChange(v)
                    if (v !== "UVG") clearUvgFields()
                    if (v !== "IVG") clearIvgFields()
                    if (v !== "MVG") clearMvgFields()
                  }}
                  disabled={disabled}>
                  <SelectTrigger hasError={invalid} data-testid="law-type-select">
                    <SelectValue asChild placeholder={t("patient.conclude.billing.lawType") + "*"}>
                      <LabelAndSelectValue
                        data-testid="law-type"
                        label={t("patient.conclude.billing.lawType") + "*"}
                        value={value ? t(`patient.conclude.billing.lawList.${value}`) : ""}
                      />
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {LAW_TYPES.map((law) => (
                      <SelectItem value={law} key={law} data-testid={`law-type-${law.toLowerCase()}`}>
                        {t(`patient.conclude.billing.lawList.${law}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TranslatedFieldError errors={errors} name="lawType" />
              </div>
            )}
          />
          {lawType === "UVG" && (
            <>
              <DatePicker
                data-testid="accident-date-input"
                label={t("patient.conclude.billing.accidentDate")}
                onSelect={(date) => setValue("dateOfAccident", date, { shouldValidate: true })}
                onReset={() => setValue("dateOfAccident", undefined, { shouldValidate: true })}
                disabledMatcher={isFuture}
                value={dateOfAccident}
                error={errors.dateOfAccident?.message?.toString()}
                translateError
                required
                readOnly={false}
                disabled={disabled}
              />
              <FormField
                control={control}
                name="organisationId"
                render={({ field: { value, onChange }, fieldState: { error } }) => {
                  const objValue = value
                    ? {
                        id: value,
                        name: getInsuranceNameByOrgName(value) ?? "",
                      }
                    : undefined
                  return (
                    <FilterableSelect<InsuranceOption>
                      label={t("patient.conclude.billing.accidentInsurance")}
                      groups={accidentInsuranceGroups}
                      clearable
                      required
                      value={objValue}
                      data-testid="accident-insurance"
                      error={error?.message}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      onChange={(value) => onChange(value?.id)}
                      disabled={disabled}
                    />
                  )
                }}
              />
              <Input
                {...register("accidentReference")}
                testId="accident-reference"
                label={t("patient.conclude.billing.accidentReference")}
                error={errors.accidentReference?.message}
                translateError
                disabled={disabled}
              />
            </>
          )}
          {lawType === "IVG" && (
            <Input
              {...register("ivgCaseReference")}
              testId="ivg-case-reference"
              label={t("patient.conclude.billing.caseReference")}
              error={errors.ivgCaseReference?.message}
              translateError
              required
              maxLength={24}
              disabled={disabled}
            />
          )}
          {lawType === "MVG" && (
            <Input
              {...register("mvgCaseReference")}
              testId="mvg-case-reference"
              label={t("patient.conclude.billing.caseReference")}
              error={errors.mvgCaseReference?.message}
              translateError
              required
              disabled={disabled}
              onChange={(e) => {
                setValue("mvgCaseReference", e.target.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            />
          )}
        </>
      )}
    </div>
  )
}
export { ChEditBillingSection }
